import React, { useContext, useState } from "react";
import { Box, Text, ThemeContext } from "grommet";
import { normalizeColor } from "grommet/utils";
import { useRouter } from "next/router";
import { Cell, Pie, PieChart, Tooltip } from "recharts";
import styled from "styled-components";
import { formatCurrency, formatCurrencySI } from "../utils/numbers";
import { AGING_BALANCE_GRAPH_COLOR_MAPPING } from "../constants/Colors";
import { formatDate, formatTime, toLocalDateString } from "../utils/dates";
import { snakeCase } from "../utils/string";
import { agingReport, invoicesForAgingBucket, overdueAmount } from "../utils/chart-data";
import Button from "./Button";
import { SkeletonBlock, SkeletonText } from "./charts/ChartSkeleton";
import ChartTooltip from "./charts/ChartTooltip";
import ChartLink from "./charts/ChartLink";

const DonutWrap = styled.div`position: relative; width: 240px; height: 240px;`;
const DonutCenter = styled.div`
    position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center;
    pointer-events: none; text-align: center;
    a { pointer-events: auto; }
`;

// The donut and its legend as one group, centered in the panel, stacked once it's too narrow for both
const SideBySide = styled.div`
    container-type: inline-size;
    & > div { display: grid; gap: 24px; justify-items: center; }
    & > div > :last-child { justify-self: stretch; min-width: 0; }
    @container (min-width: 700px) {
        & > div { grid-template-columns: 240px minmax(0, 460px); justify-content: center; align-items: center; gap: 56px; }
    }
`;

// The same column widths on every row, so the amounts, counts and shares line up. The invoice
// count is the first thing to go when the row runs out of room.
const LegendRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto 96px 40px;
    column-gap: 16px;
    align-items: center;
    padding: 7px 6px;
    font-variant-numeric: tabular-nums;
    transition: opacity .15s;
    & > :nth-child(n + 2) { text-align: right; white-space: nowrap; }
    @container (max-width: 400px) {
        grid-template-columns: minmax(0, 1fr) auto 40px;
        & > :nth-child(3) { display: none; }
    }
`;

// "18%" of the total, and "<1%" rather than a 0% beside money that is there
const share = (amount, total) => {
    if (!total || !amount) return "0%";
    const percent = (amount / total) * 100;
    return percent < 1 ? "<1%" : `${Math.round(percent)}%`;
};

// Stand-ins while the summary loads, so the card keeps its height
const LOADING_BUCKETS = [["0", "Not yet due"], ["1", "1-30 days"], ["31", "31-60 days"], ["61", "61-90 days"], ["91", "91+ days"]]
    .map(([bucket, label]) => [bucket, {label, amount: 0}]);

const plural = (count, word) => `${count.toLocaleString("en-US")} ${word}${count === 1 ? "" : "s"}`;

// Past $1M the cents crowd the row and tell the reader nothing
const rowAmount = (amount, currency) => {
    const formatted = formatCurrency(amount, currency);
    return amount >= 100000000 ? formatted.replace(/\.\d+/, "") : formatted;
};

export const AgingBalanceRow = ({ bucket, agingBalance, color, currency, isLoading, href, section = "aging_balance_pie_chart" }) => {
    const router = useRouter();
    const { label, amount, invoice_count: count } = agingBalance;
    const row = (
        <Box direction="row" justify="between" align="center" pad={{vertical: "xsmall", horizontal: "xxsmall"}} gap="small">
            <Box direction="row" align="center" gap="xsmall">
                <Box round="100%" background={isLoading ? "chart-muted" : color} width={{min: "10px"}} height={{min: "10px"}} />
                <Text>{label}</Text>
            </Box>
            <Box direction="row" align="center" gap="small" flex={false}>
                {isLoading ? <SkeletonBlock width="88px" height="12px" /> : <Text>{rowAmount(amount, currency)}</Text>}
                {isLoading && <Box width={{min: "86px"}} align="end"><SkeletonBlock width="64px" height="12px" /></Box>}
                {count != null && (
                    <Box width={{min: "86px"}} align="end"><Text size="small" color="text-weak" style={{whiteSpace: "nowrap"}}>{plural(count, "invoice")}</Text></Box>
                )}
            </Box>
        </Box>
    );
    // Not a disabled button while loading, which would fade the placeholders
    if (isLoading) return row;
    return (
        <Button
            event={{section, element: `breakdown_${snakeCase(label)}`}}
            hoverIndicator
            onClick={() => router.push(href || invoicesForAgingBucket(bucket))}>
            {row}
        </Button>
    );
};

// The ring itself, shared by both layouts. Each slice carries its `bucket`, which picks its
// colour. `active` fades every other slice; `tooltip` is for the layout without a hover center.
const Donut = ({ slices, hasData, isLoading, active, onActive, onSelect, tooltip, children }) => {
    const theme = useContext(ThemeContext);
    const sliceColor = (bucket) => {
        const color = isLoading || !hasData ? "chart-muted" : AGING_BALANCE_GRAPH_COLOR_MAPPING[bucket];
        return normalizeColor(theme.global.colors[color] || color, theme);
    };
    const bucketOf = (slice) => slice.payload?.bucket ?? slice.bucket;
    return (
        <DonutWrap>
            <PieChart width={240} height={240}>
                <Pie
                    data={slices}
                    cx="50%"
                    cy="50%"
                    innerRadius={96}
                    outerRadius={116}
                    paddingAngle={slices.length > 1 ? 2 : 0}
                    dataKey="value"
                    stroke="none"
                    isAnimationActive={false}
                    style={{cursor: hasData ? "pointer" : "default"}}
                    onMouseEnter={(slice) => hasData && onActive?.(bucketOf(slice))}
                    onMouseLeave={() => onActive?.(null)}
                    onClick={(slice) => hasData && onSelect(bucketOf(slice))}>
                    {/* Keyed by bucket, not position, so a missing bucket can't shift the colours */}
                    {slices.map((entry) => (
                        <Cell key={entry.bucket} fill={sliceColor(entry.bucket)} fillOpacity={active && active !== entry.bucket ? 0.25 : 1} />
                    ))}
                </Pie>
                {hasData && tooltip && <Tooltip isAnimationActive={false} content={tooltip} />}
            </PieChart>
            <DonutCenter>{children}</DonutCenter>
        </DonutWrap>
    );
};

// The donut with the overdue share in its center, beside the buckets it's made of. Hovering a row
// or a slice fades the rest and puts that bucket in the center instead.
const AgingLegend = ({ buckets, slices, hasData, total, overdue, currency, isLoading, hrefFor }) => {
    const router = useRouter();
    const [active, setActive] = useState(null);
    const current = active && buckets.find(([bucket]) => bucket === active)?.[1];
    const faded = (bucket) => Boolean(active) && active !== bucket;

    return (
        <SideBySide>
            <div>
                <Donut slices={slices} hasData={hasData} isLoading={isLoading} active={active} onActive={setActive} onSelect={(bucket) => router.push(hrefFor(bucket))}>
                        {isLoading ? (
                            <Box align="center"><SkeletonText width="80px" height="28px" lineHeight="37px" /><SkeletonText width="56px" lineHeight="20px" /></Box>
                        ) : (
                            <Box align="center" gap="xxsmall">
                                <Text size="34px" weight={600} color="text" style={{lineHeight: 1.1}}>
                                    {current ? formatCurrencySI(current.amount || 0, currency) : share(overdue, total)}
                                </Text>
                                <Text color="text-weak">{current ? `${current.label}, ${share(current.amount, total)}` : "overdue"}</Text>
                            </Box>
                        )}
                </Donut>
                <Box onMouseLeave={() => setActive(null)}>
                    {buckets.map(([bucket, agingBalance]) => {
                        const row = (
                            <LegendRow style={{opacity: faded(bucket) ? 0.45 : 1}}>
                                <Box direction="row" align="center" gap="small" style={{minWidth: 0}}>
                                    <Box round="100%" background={isLoading ? "chart-muted" : AGING_BALANCE_GRAPH_COLOR_MAPPING[bucket]} width={{min: "10px"}} height={{min: "10px"}} />
                                    <Text truncate>{agingBalance.label}</Text>
                                </Box>
                                {isLoading ? <SkeletonBlock width="56px" height="12px" /> : <Text>{formatCurrencySI(agingBalance.amount || 0, currency)}</Text>}
                                <Text size="small" color="text-weak">{!isLoading && agingBalance.invoice_count != null ? plural(agingBalance.invoice_count, "invoice") : ""}</Text>
                                <Text size="small" color="text-weak">{isLoading ? "" : share(agingBalance.amount, total)}</Text>
                            </LegendRow>
                        );
                        // Not a disabled button while loading, which would fade the placeholders
                        if (isLoading) return <Box key={bucket}>{row}</Box>;
                        return (
                            <Button
                                key={bucket}
                                event={{section: "aging_balance_strip", element: `breakdown_${snakeCase(agingBalance.label)}`}}
                                hoverIndicator
                                onMouseEnter={() => setActive(bucket)}
                                onFocus={() => setActive(bucket)}
                                onBlur={() => setActive(null)}
                                onClick={() => router.push(hrefFor(bucket))}>
                                {row}
                            </Button>
                        );
                    })}
                </Box>
            </div>
        </SideBySide>
    );
};

const AgingBalancesPieChart = ({ dashboardData, asOfDate, isLoading=true, wide }) => {
    const {
        aging_balances: agingBalances,
        total_receivables: totalAmount,
        invoice_count: invoiceCount,
        time_calculated: timeCalculated,
        currency,
    } = dashboardData || {};

    // aging_balances is keyed by age threshold, but also carries loose extras
    // such as `credits`, so keep only the entries shaped like an age bucket.
    //
    // The key travels with each slice as `bucket`, because that is what picks its colour. It
    // used to be picked by `name`, the backend's label, which is display copy: when "Due"
    // became "Not yet due" the lookup missed and the slice rendered with no fill at all.
    //
    // Bucket 0 used to be labelled "Due", for the money that is NOT due. The backend now sends
    // "Not yet due", but the company summary cache has no expiry, so a merchant can still be
    // served the old string indefinitely. Swapped here, once, so the legend, the slice name and
    // the slice's hover tooltip all read the same.
    const balanceBuckets = Object.entries(agingBalances || {})
        .filter(([, agingBalance]) => agingBalance?.label)
        .map(([bucket, agingBalance]) => [
            bucket,
            agingBalance.label === "Due" ? {...agingBalance, label: "Not yet due"} : agingBalance
        ]);
    const router = useRouter();
    const isEmpty = !isLoading && !totalAmount;
    const slices = balanceBuckets
        .filter(([, agingBalance]) => agingBalance.amount > 0)
        .map(([bucket, agingBalance]) => ({
            name: agingBalance.label,
            bucket,
            value: agingBalance.amount,
            count: agingBalance.invoice_count,
        }));
    const data = totalAmount && slices.length > 0 ? slices : [{name: "placeholder", bucket: "placeholder", value: 1}];
    const hasData = data[0].bucket !== "placeholder";
    const report = agingReport(asOfDate ? toLocalDateString(asOfDate) : undefined);

    const renderTooltip = ({ active, payload }) => {
        const slice = payload?.[0]?.payload;
        if (!active || !slice) return null;
        return (
            <ChartTooltip
                title={slice.name}
                lines={[formatCurrency(slice.value, currency), ...(slice.count != null ? [plural(slice.count, "invoice")] : [])]}
            />
        );
    };

    // Wide, the donut's center shows the share that's overdue: the same figure as the tile above
    const overdue = overdueAmount(dashboardData);

    const donut = (
        <Donut slices={data} hasData={hasData} isLoading={isLoading} tooltip={renderTooltip}
            onSelect={(bucket) => router.push(asOfDate ? report : invoicesForAgingBucket(bucket))}>
                <Box align="center">
                    <Text color="text-weak">Total receivables</Text>
                    {isLoading ? <SkeletonText width="112px" height="28px" lineHeight="37px" /> : (
                        <ChartLink
                            href={report}
                            label={<Text size="32px" weight={600} color="text" style={{lineHeight: 1.15}}>{formatCurrencySI(totalAmount || 0, currency)}</Text>}
                        />
                    )}
                    {isLoading && <SkeletonText width="72px" lineHeight="20px" />}
                    {invoiceCount != null && <Text size="small" color="text-weak">{plural(invoiceCount, "invoice")}</Text>}
                </Box>
        </Donut>
    );

    const emptyState = asOfDate
        ? <Text color="text-weak">No open invoices on this date.</Text>
        : (
            <Box gap="small" align="start">
                <Text color="text-weak">Open invoices appear here once you send one.</Text>
                <Button
                    event={{section: "aging_balance_pie_chart", element: "create_invoice"}}
                    secondary
                    size="small"
                    label="Create invoice"
                    onClick={() => router.push("/draft-invoices/new/edit")}
                />
            </Box>
        );

    // The legend dot comes from the same map as its slice, keyed by the same bucket
    const bucketRows = (
        <Box>
            {(isLoading ? LOADING_BUCKETS : balanceBuckets).map(([bucket, agingBalance]) => (
                <AgingBalanceRow
                    key={bucket}
                    bucket={bucket}
                    agingBalance={agingBalance}
                    color={AGING_BALANCE_GRAPH_COLOR_MAPPING[bucket]}
                    currency={currency}
                    isLoading={isLoading}
                    href={asOfDate ? report : undefined}
                />
            ))}
        </Box>
    );

    const footer = (
        <Box direction="row" justify="between" align="center" gap="small" wrap>
            {isLoading ? <SkeletonBlock width="180px" height="10px" /> : (
                <Text color="text-weak" size="xsmall">
                    {timeCalculated ? `Last updated ${formatDate(timeCalculated)} ${formatTime(timeCalculated)}` : ""}
                </Text>
            )}
            <ChartLink href={report} label={<Text size="small" color="text-weak">View report</Text>} />
        </Box>
    );

    if (wide) {
        return (
            <Box gap="small">
                {isEmpty ? emptyState : (
                    <AgingLegend
                        buckets={isLoading ? LOADING_BUCKETS : balanceBuckets}
                        slices={data}
                        hasData={hasData}
                        total={totalAmount}
                        overdue={overdue}
                        currency={currency}
                        isLoading={isLoading}
                        hrefFor={(bucket) => (asOfDate ? report : invoicesForAgingBucket(bucket))}
                    />
                )}
                {footer}
            </Box>
        );
    }

    return (
        <Box gap="small">
            <Box align="center">{donut}</Box>
            {isEmpty ? emptyState : bucketRows}
            {footer}
        </Box>
    );
};

export default AgingBalancesPieChart;
